import { useCallback, useEffect, useState } from 'react';
import { isHttpError, useHttp } from '@mono/http-hook';
import useSession from '../../session/useSession';
import { useCurrentOrganization } from '../../organizations/hooks';

export type TeamDetails = {
  id: string;
  name: string;
  organizationId: string;
  membersCount: number;
};

export type TeamMember = {
  id: string;
  role: string;
  teamId: string;
  user: {
    id: string;
    name: string | null;
    email: string | null;
  };
};

type TeamsResponse = {
  teams: TeamDetails[];
};

type TeamResponse = {
  team: TeamDetails;
};

type TeamMembersResponse = {
  members: TeamMember[];
};

type TeamMemberResponse = {
  member: TeamMember;
};

const getHttpErrorMessage = (error: unknown, fallback: string) => {
  if (isHttpError(error)) {
    const responseData = error.response?.data;
    if (responseData && typeof responseData === 'object') {
      const { error: errorMessage, message: responseMessage } =
        responseData as {
          error?: string;
          message?: string;
        };
      return errorMessage ?? responseMessage ?? error.message ?? fallback;
    }
    return error.message ?? fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

const sortTeams = (teams: TeamDetails[]) =>
  [...teams].sort((a, b) => a.name.localeCompare(b.name));

export const useTeamsSettings = () => {
  const http = useHttp();
  const session = useSession();
  const currentOrganization = useCurrentOrganization();
  const organizationId = currentOrganization?.id ?? '';

  const [teams, setTeams] = useState<TeamDetails[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(organizationId));
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isMutating, setIsMutating] = useState(false);
  const [mutateError, setMutateError] = useState<string | null>(null);

  const fetchTeams = useCallback(async () => {
    if (!organizationId) {
      setTeams([]);
      setIsLoading(false);
      setLoadError(null);
      setMutateError(null);
      return;
    }

    setIsLoading(true);
    setLoadError(null);

    try {
      const { data } = await http.get<TeamsResponse>(
        `/api/v1/organizations/${organizationId}/teams`,
        { withCredentials: true }
      );

      setTeams(sortTeams(data.teams));
      setMutateError(null);
    } catch (error) {
      setTeams([]);
      setMutateError(null);
      setLoadError(getHttpErrorMessage(error, 'Unable to load teams right now.'));
    } finally {
      setIsLoading(false);
    }
  }, [http, organizationId]);

  useEffect(() => {
    void fetchTeams();
  }, [fetchTeams]);

  const createTeam = useCallback(
    async (input: { name: string }) => {
      if (!organizationId) {
        throw new Error('Organization is not selected.');
      }

      setMutateError(null);
      setIsMutating(true);

      try {
        const { data } = await http.post<TeamResponse>(
          `/api/v1/organizations/${organizationId}/teams`,
          input,
          { withCredentials: true }
        );

        setTeams((prev) => sortTeams([...prev, data.team]));
        await session.update?.();
        return data.team;
      } catch (error) {
        const message = getHttpErrorMessage(error, 'Unable to create team.');
        setMutateError(message);
        throw new Error(message);
      } finally {
        setIsMutating(false);
      }
    },
    [http, organizationId, session]
  );

  const updateTeam = useCallback(
    async (teamId: string, input: { name: string }) => {
      if (!organizationId) {
        throw new Error('Organization is not selected.');
      }

      setMutateError(null);
      setIsMutating(true);

      try {
        const { data } = await http.patch<TeamResponse>(
          `/api/v1/organizations/${organizationId}/teams/${teamId}`,
          input,
          { withCredentials: true }
        );

        setTeams((prev) =>
          sortTeams(prev.map((team) => (team.id === teamId ? data.team : team)))
        );
        await session.update?.();
        return data.team;
      } catch (error) {
        const message = getHttpErrorMessage(error, 'Unable to update team.');
        setMutateError(message);
        throw new Error(message);
      } finally {
        setIsMutating(false);
      }
    },
    [http, organizationId, session]
  );

  const deleteTeam = useCallback(
    async (teamId: string) => {
      if (!organizationId) {
        throw new Error('Organization is not selected.');
      }

      setMutateError(null);
      setIsMutating(true);

      try {
        await http.delete(
          `/api/v1/organizations/${organizationId}/teams/${teamId}`,
          { withCredentials: true }
        );

        setTeams((prev) => prev.filter((team) => team.id !== teamId));
        await session.update?.();
      } catch (error) {
        const message = getHttpErrorMessage(error, 'Unable to delete team.');
        setMutateError(message);
        throw new Error(message);
      } finally {
        setIsMutating(false);
      }
    },
    [http, organizationId, session]
  );

  return {
    organizationId,
    teams,
    isLoading,
    loadError,
    refresh: fetchTeams,
    createTeam,
    updateTeam,
    isMutating,
    mutateError,
    deleteTeam,
  };
};

export const useTeamMembers = (teamId: string | null) => {
  const http = useHttp();
  const session = useSession();
  const currentOrganization = useCurrentOrganization();
  const organizationId = currentOrganization?.id ?? '';

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(teamId));
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isMutating, setIsMutating] = useState(false);
  const [mutateError, setMutateError] = useState<string | null>(null);

  const sortMembers = useCallback((list: TeamMember[]) => {
    return [...list].sort((a, b) => {
      const aName = a.user.name ?? a.user.email ?? '';
      const bName = b.user.name ?? b.user.email ?? '';
      return aName.localeCompare(bName);
    });
  }, []);

  const fetchMembers = useCallback(async () => {
    if (!organizationId || !teamId) {
      setMembers([]);
      setIsLoading(false);
      setLoadError(null);
      setMutateError(null);
      return;
    }

    setIsLoading(true);
    setLoadError(null);

    try {
      const { data } = await http.get<TeamMembersResponse>(
        `/api/v1/organizations/${organizationId}/teams/${teamId}/members`,
        { withCredentials: true }
      );

      setMembers(sortMembers(data.members));
      setMutateError(null);
    } catch (error) {
      setMembers([]);
      setMutateError(null);
      setLoadError(getHttpErrorMessage(error, 'Unable to load team members.'));
    } finally {
      setIsLoading(false);
    }
  }, [http, organizationId, teamId, sortMembers]);

  useEffect(() => {
    void fetchMembers();
  }, [fetchMembers]);

  const addMember = useCallback(
    async (input: { userId: string; role?: string }) => {
      if (!organizationId || !teamId) {
        throw new Error('Team is not selected.');
      }

      setMutateError(null);
      setIsMutating(true);

      try {
        const { data } = await http.post<TeamMemberResponse>(
          `/api/v1/organizations/${organizationId}/teams/${teamId}/members`,
          input,
          { withCredentials: true }
        );

        setMembers((prev) => sortMembers([...prev, data.member]));
        await session.update?.();
        return data.member;
      } catch (error) {
        const message = getHttpErrorMessage(error, 'Unable to add user to team.');
        setMutateError(message);
        throw new Error(message);
      } finally {
        setIsMutating(false);
      }
    },
    [http, organizationId, session, teamId]
  );

  const removeMember = useCallback(
    async (memberId: string) => {
      if (!organizationId || !teamId) {
        throw new Error('Team is not selected.');
      }

      setMutateError(null);
      setIsMutating(true);

      try {
        await http.delete(
          `/api/v1/organizations/${organizationId}/teams/${teamId}/members/${memberId}`,
          { withCredentials: true }
        );

        setMembers((prev) => prev.filter((member) => member.id !== memberId));
        await session.update?.();
      } catch (error) {
        const message = getHttpErrorMessage(error, 'Unable to remove user from team.');
        setMutateError(message);
        throw new Error(message);
      } finally {
        setIsMutating(false);
      }
    },
    [http, organizationId, session, teamId]
  );

  return {
    members,
    isLoading,
    loadError,
    refresh: fetchMembers,
    addMember,
    removeMember,
    isMutating,
    mutateError,
  };
};
